"use client";

import { c as _c } from "react/compiler-runtime";
import type { OnDragEndResponder } from "@hello-pangea/dnd";
import type { StackProps } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { selectIsEditable } from "#/features/project/model/projectSlice";
import { DraggableSelectBarList } from "#/features/selectBar/ui/DraggableSelectBarList";
import { DraggableSelectBarChip } from "#/features/selectBar/ui/SelectBarChip";
import type { UseTRPCQueryResult } from "#/server/api/trpc";
import type { PlaygroundTestCase } from "#/server/db/generated/browser";
import { api } from "#/shared/api";
import type { RouterOutputs } from "#/shared/api";
import { usePlaygroundSlugs } from "#/shared/hooks";
import { useI18nContext } from "#/shared/hooks";
import { useAppSelector } from "#/store/hooks";
import { CaseModal } from "./CaseModal";
type TestCaseBrief = Pick<PlaygroundTestCase, "id" | "order" | "slug" | "title">;
type TestCaseSelectBarProps = StackProps & {
  selectedProject: UseTRPCQueryResult<RouterOutputs["project"]["getBySlug"]>;
};
export const TestCaseSelectBar: React.FC<TestCaseSelectBarProps> = t0 => {
  const $ = _c(52);
  let restProps;
  let selectedProject;
  if ($[0] !== t0) {
    ({
      selectedProject,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = restProps;
    $[2] = selectedProject;
  } else {
    restProps = $[1];
    selectedProject = $[2];
  }
  const {
    LL
  } = useI18nContext();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    projectSlug: t1,
    caseSlug: t2,
    setCase
  } = usePlaygroundSlugs();
  const projectSlug = t1 === undefined ? "" : t1;
  const caseSlug = t2 === undefined ? "" : t2;
  const invalidateQueries = async slug => {
    await trpcUtils.project.getBySlug.invalidate(projectSlug);
    await trpcUtils.project.getCaseBySlug.invalidate({
      projectId: selectedProject.data?.id || "",
      slug: slug || caseSlug
    });
  };
  const trpcUtils = api.useUtils();
  let t3;
  if ($[3] !== projectSlug || $[4] !== trpcUtils.project.getBySlug) {
    t3 = cases => {
      trpcUtils.project.getBySlug.setData(projectSlug, prevData => {
        if (!prevData) {
          return prevData;
        }
        return {
          ...prevData,
          cases
        };
      });
    };
    $[3] = projectSlug;
    $[4] = trpcUtils.project.getBySlug;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const updateCasesCache = t3;
  let t4;
  if ($[6] !== enqueueSnackbar || $[7] !== invalidateQueries || $[8] !== setCase) {
    t4 = {
      onSuccess: async data => {
        await invalidateQueries(data.slug);
        setCase(data.slug);
        enqueueSnackbar(`🧪 Test case "${data.title}" created successfully! 🎉`, {
          variant: "success"
        });
      }
    };
    $[6] = enqueueSnackbar;
    $[7] = invalidateQueries;
    $[8] = setCase;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const addCase = api.project.addCase.useMutation(t4);
  let t5;
  if ($[10] !== updateCasesCache) {
    t5 = {
      onSuccess: async data_0 => {
        updateCasesCache(data_0);
      }
    };
    $[10] = updateCasesCache;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const reorderCases = api.project.reorderCases.useMutation(t5);
  let t6;
  if ($[12] !== caseSlug || $[13] !== invalidateQueries || $[14] !== setCase) {
    t6 = {
      onSuccess: async data_1 => {
        await invalidateQueries(data_1.slug);
        if (caseSlug === data_1.slug) {
          setCase("");
        }
      }
    };
    $[12] = caseSlug;
    $[13] = invalidateQueries;
    $[14] = setCase;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const deleteCase = api.project.deleteCase.useMutation(t6);
  const cases_0 = selectedProject.data?.cases;
  const isLoading = selectedProject.isLoading || addCase.isPending || deleteCase.isPending;
  const isEditable = useAppSelector(selectIsEditable);
  let t7;
  if ($[16] !== setCase) {
    t7 = testCase => {
      setCase(testCase.slug);
    };
    $[16] = setCase;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleCaseClick = t7;
  let t8;
  if ($[18] !== handleCaseClick) {
    t8 = testCase_0 => {
      handleCaseClick(testCase_0);
      setIsModalOpen(true);
    };
    $[18] = handleCaseClick;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleCaseEdit = t8;
  let t9;
  if ($[20] !== addCase || $[21] !== caseSlug || $[22] !== cases_0?.length || $[23] !== selectedProject.data) {
    t9 = () => {
      if (!selectedProject.data) {
        return;
      }
      const order = cases_0?.length ?? 0;
      addCase.mutate({
        projectId: selectedProject.data.id,
        order,
        referenceCaseSlug: caseSlug
      });
    };
    $[20] = addCase;
    $[21] = caseSlug;
    $[22] = cases_0?.length;
    $[23] = selectedProject.data;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleAddCase = t9;
  let t10;
  if ($[25] !== cases_0 || $[26] !== reorderCases || $[27] !== selectedProject.data || $[28] !== updateCasesCache) {
    t10 = result => {
      if (!result.destination || result.destination.index === result.source.index || !cases_0 || !selectedProject.data) {
        return;
      }
      const {
        source,
        destination
      } = result;
      const newList = Array.from(cases_0);
      const removed = newList.splice(source.index, 1);
      newList.splice(destination.index, 0, ...removed);
      updateCasesCache(newList);
      reorderCases.mutate({
        projectId: selectedProject.data.id,
        caseIds: newList.map(_temp)
      });
    };
    $[25] = cases_0;
    $[26] = reorderCases;
    $[27] = selectedProject.data;
    $[28] = updateCasesCache;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const onItemDragEnd = t10;
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsModalOpen(false);
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== isModalOpen) {
    t12 = <CaseModal open={isModalOpen} onClose={t11} />;
    $[31] = isModalOpen;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = !selectedProject.data;
  let t14;
  if ($[33] !== LL || $[34] !== caseSlug || $[35] !== cases_0 || $[36] !== handleCaseClick || $[37] !== handleCaseEdit || $[38] !== isEditable || $[39] !== isLoading) {
    t14 = (_, droppableSnapshot) => cases_0?.map((testCase_1, index) => <DraggableSelectBarChip id={testCase_1.id} key={testCase_1.id} index={index} droppableSnapshot={droppableSnapshot} editLabel={LL.EDIT_TEST_CASE()} isCurrent={testCase_1.slug === caseSlug} isEditable={isEditable} label={testCase_1.title} disabled={isLoading} onClick={() => handleCaseClick(testCase_1)} onEditClick={() => handleCaseEdit(testCase_1)} />);
    $[33] = LL;
    $[34] = caseSlug;
    $[35] = cases_0;
    $[36] = handleCaseClick;
    $[37] = handleCaseEdit;
    $[38] = isEditable;
    $[39] = isLoading;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== handleAddCase || $[42] !== isEditable || $[43] !== isLoading || $[44] !== onItemDragEnd || $[45] !== restProps || $[46] !== t13 || $[47] !== t14) {
    t15 = <DraggableSelectBarList droppableId="droppable-solutions" isLoading={isLoading} isEmpty={t13} onItemDragEnd={onItemDragEnd} addItemTitle={"Add new test case \uD83E\uDDEA"} handleAddItem={handleAddCase} isEditable={isEditable} {...restProps}>{t14}</DraggableSelectBarList>;
    $[41] = handleAddCase;
    $[42] = isEditable;
    $[43] = isLoading;
    $[44] = onItemDragEnd;
    $[45] = restProps;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== t12 || $[50] !== t15) {
    t16 = <>{t12}{t15}</>;
    $[49] = t12;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  return t16;
};
function _temp(solution) {
  return solution.id;
}